/**
 * 이상한 방 - 체인저 띠 뷰 조립 (도구의 상태 → `ChangerBarView`, 순수 함수)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(거부는 선택 불가 - 잠금은 verbs의 답이다)
 *
 * `changerTool.ts`에서 갈라져 나왔다(상태 머신 하나로 400줄을 넘겼다). 도구가 든 것(초안 · 모드 · 선택 · 표시용 개수)을
 * 한 번 받아 띠가 그릴 모양을 만든다 - 잠금은 `changerLocks`가 verbs를 실제로 돌려 본 답이고, 여기는 그 답과 이름을 모양에
 * 앉히기만 한다. 규칙은 없다.
 */

import type { PaletteColor } from '../catalog/palette'
import type { Catalog } from '../core/room'
import type { CatalogEntry, CatalogId, InstanceId, PlayerId, RoomState } from '../core/types'
import { applyRecolor, type VerbContext } from '../core/verbs'
import { lockFor } from './changerLocks'
import { CHANGER_VERBS, type ChangerBarView, type ChangerCatalogView, type ChangerColorView, type ChangerModeState } from './changerToolTypes'

/** 도구가 뷰를 만들 때 넘기는 것 전부. 함수 셋(`ctx` · `catalogIdOf`)은 도구의 클로저다 - 값으로 굳히면 매 뷰마다 문맥을 두 번 만든다. */
export type ChangerToolViewInputs = Readonly<{
  draft: RoomState
  mode: ChangerModeState
  /** 시드가 고른 생김용 소품들(`pickChangerCatalog`). */
  strip: readonly CatalogEntry[]
  palette: readonly PaletteColor[]
  catalog: Catalog
  me: PlayerId
  ctx: VerbContext
  budget: number
  /** 표시용 사용 개수(확정 + 답 대기). */
  used: number
  /** 표시용 남은 개수(호스트의 `budgetLeft` - 답 대기). 0 이하면 전부 잠긴다. */
  budgetLeft: number
  selectedId: InstanceId | null
  /** 고른 물건이 이미 자기 변화를 갖고 있는가(`changerLocks.LockInputs.reediting`). */
  reediting: boolean
  undoId: InstanceId | null
  catalogIdOf: (id: InstanceId) => CatalogId | null
  /** 내 구획의 id(M11). 이름표는 `ui/changerBar.ts`가 사전에서 굽는다 - `input/`은 사전을 보지 않는다. */
  zoneId: string | null
}>

function colorsView(inputs: ChangerToolViewInputs): readonly ChangerColorView[] | null {
  const { draft, selectedId, palette } = inputs
  const obj = selectedId === null ? undefined : draft.objects.get(selectedId)
  if (selectedId === null || obj === undefined) return null
  // 갈아 끼우는 중이면 예산이 0이어도 색을 고칠 수 있다(`changerLocks.lockFor`와 같은 셈).
  const exhausted = inputs.budgetLeft <= 0 && !inputs.reediting
  return palette.map((color) => ({
    id: color.id,
    name: color.name,
    hex: color.hex,
    selected: color.id === obj.colorId,
    locked: exhausted || !applyRecolor(draft, selectedId, color.id, inputs.ctx).ok,
  }))
}

function catalogView(inputs: ChangerToolViewInputs, current: CatalogId | null): readonly ChangerCatalogView[] | null {
  if (current === null) return null
  return inputs.strip.map((entry) => ({
    id: entry.id,
    colorHex: inputs.palette[entry.defaultColorId]?.hex ?? 0,
    selected: entry.id === current,
  }))
}

/** 띠가 그릴 것 전부. `left`는 도구가 센 표시용 수를 0 아래로 내려가지 않게만 자른다(음수 개수는 화면의 말이 아니다). */
export function buildChangerBarView(inputs: ChangerToolViewInputs): ChangerBarView {
  const { mode, selectedId, undoId } = inputs
  const adding = mode.kind === 'adding' ? mode.catalogId : null
  const lockInputs = {
    draft: inputs.draft,
    ctx: inputs.ctx,
    reediting: inputs.reediting,
    me: inputs.me,
    paletteSize: inputs.palette.length,
    selectedId,
    budgetLeft: inputs.budgetLeft,
  }
  return {
    budget: inputs.budget,
    used: inputs.used,
    left: Math.max(0, inputs.budgetLeft),
    mode: mode.kind,
    subjectItemId: adding !== null ? adding : selectedId === null ? null : inputs.catalogIdOf(selectedId),
    verbs: CHANGER_VERBS.map((verb) => ({
      verb,
      locked: lockFor(verb, lockInputs),
      active: (verb === 'move' && mode.kind === 'moving') || (verb === 'add' && adding !== null),
    })),
    catalog: catalogView(inputs, adding),
    colors: colorsView(inputs),
    undoItemId: undoId === null ? null : inputs.catalogIdOf(undoId),
    zoneId: inputs.zoneId,
  }
}
